import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, User, Phone, CheckCircle, Link } from "lucide-react";

interface AddPlayerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  onPlayerAdded: () => void;
}

interface ExistingUserData {
  display_name?: string;
  phone?: string;
  auth_user_id?: string;
  exists: boolean;
}

export const AddPlayerDialog = ({ open, onOpenChange, groupId, onPlayerAdded }: AddPlayerDialogProps) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [existingUser, setExistingUser] = useState<ExistingUserData | null>(null);
  const [emailChecked, setEmailChecked] = useState(false);
  const [step, setStep] = useState<'email' | 'details'>('email');
  const { toast } = useToast();

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setEmail("");
      setName("");
      setPhone("");
      setExistingUser(null);
      setEmailChecked(false);
      setStep('email');
    }
  }, [open]);

  const checkEmailExists = async (emailToCheck: string) => {
    if (!emailToCheck.trim()) return;
    
    setEmailChecking(true);
    try {
      const { data, error } = await supabase.rpc('get_user_data_by_email', {
        user_email: emailToCheck.trim()
      });

      if (error) throw error;

      if (data && typeof data === 'object' && data !== null) {
        const userData = data as unknown as ExistingUserData;
        setExistingUser(userData);
        setName(userData.display_name || "");
        setPhone(userData.phone || "");
        toast({
          title: "Existing User Found",
          description: "User data has been automatically populated from their profile.",
        });
      } else {
        setExistingUser(null);
        setName("");
        setPhone("");
      }
      setEmailChecked(true);
      setStep('details');
    } catch (error) {
      console.error('Error checking email:', error);
      setExistingUser(null);
      setEmailChecked(true);
      setStep('details');
    } finally {
      setEmailChecking(false);
    }
  };

  const handleEmailNext = () => {
    if (email.trim()) {
      checkEmailExists(email.trim());
    } else {
      setEmailChecked(true);
      setStep('details');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    try {
      // Enhanced authentication debugging and verification
      console.log('Starting player addition process...');
      
      // Step 1: Get and verify current user session
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log('Current user:', user?.id, 'Error:', userError);
      
      if (!user || userError) {
        // Try to refresh session if user is null
        console.log('User not found, attempting session refresh...');
        const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();
        console.log('Session refresh result:', session?.user?.id, 'Error:', sessionError);
        
        if (!session?.user || sessionError) {
          throw new Error("Authentication required. Please log in again.");
        }
      }

      const currentUserId = user?.id || (await supabase.auth.getSession()).data.session?.user?.id;
      if (!currentUserId) {
        throw new Error("Unable to verify authentication. Please log in again.");
      }

      // Step 2: Verify user has permission to add players to this group
      console.log('Checking user permissions for group:', groupId, 'User:', currentUserId);
      
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('created_by')
        .eq('id', groupId)
        .single();

      if (groupError) {
        console.error('Error fetching group data:', groupError);
        throw new Error("Unable to verify group permissions. Please try again.");
      }

      console.log('Group creator:', groupData.created_by, 'Current user:', currentUserId);

      if (groupData.created_by !== currentUserId) {
        throw new Error("You don't have permission to add players to this group. Only the group creator can add players.");
      }

      console.log('Permission verified, proceeding with player addition...');

      // Check for existing players in the group first
      const { data: existingPlayers, error: checkError } = await supabase.rpc('find_existing_player_in_group', {
        p_group_id: groupId,
        p_email: email.trim() || null,
        p_name: name.trim()
      });

      if (checkError) {
        console.error('Error checking for existing players:', checkError);
      }

      let shouldUpdate = false;
      let playerToUpdate = null;

      if (existingPlayers && existingPlayers.length > 0) {
        // If there's an exact match by name, update that player
        const exactNameMatch = existingPlayers.find(p => p.player_name === name.trim());
        if (exactNameMatch) {
          shouldUpdate = true;
          playerToUpdate = exactNameMatch;
        } 
        // If there's an email match, check if it's safe to merge
        else if (email.trim()) {
          const emailMatch = existingPlayers.find(p => p.player_email && p.player_email.toLowerCase() === email.trim().toLowerCase());
          if (emailMatch && !emailMatch.has_activity) {
            // Safe to delete the duplicate and use the current name
            await supabase.from('players').delete().eq('id', emailMatch.player_id);
          } else if (emailMatch) {
            throw new Error(`A player with email ${email.trim()} already exists in this group.`);
          }
        }
      }

      // Determine auth_user_id based on existing user data
      let authUserId = null;
      let finalEmail = email.trim() || null;
      
      if (existingUser?.auth_user_id) {
        authUserId = existingUser.auth_user_id;
      } else if (email.trim()) {
        // Check if auth user exists for this email
        const { data: existingAuthUserId, error: rpcError } = await supabase.rpc('find_or_create_auth_user_invitation', {
          user_email: email.trim()
        });
        if (rpcError) {
          console.error('Error calling find_or_create_auth_user_invitation:', rpcError);
        }
        authUserId = existingAuthUserId;
      }

      let playerData;
      if (shouldUpdate && playerToUpdate) {
        // Update existing player
        console.log('Updating existing player:', playerToUpdate.player_id, 'with data:', {
          name: name.trim(),
          email: finalEmail,
          phone: phone.trim() || null,
          auth_user_id: authUserId
        });
        
        const { data, error } = await supabase
          .from('players')
          .update({ 
            name: name.trim(),
            email: finalEmail,
            phone: phone.trim() || null,
            auth_user_id: authUserId
          })
          .eq('id', playerToUpdate.player_id)
          .select()
          .single();

        if (error) {
          console.error('Update player error:', error);
          throw error;
        }
        playerData = data;
        console.log('Player updated successfully:', data);
      } else {
        // Create new player
        const insertData = { 
          name: name.trim(),
          email: finalEmail,
          phone: phone.trim() || null,
          group_id: groupId,
          auth_user_id: authUserId
        };
        
        console.log('Creating new player with data:', insertData);
        console.log('Current auth context before insert:', {
          currentUserId,
          sessionUser: (await supabase.auth.getSession()).data.session?.user?.id
        });
        
        const { data, error } = await supabase
          .from('players')
          .insert(insertData)
          .select()
          .single();

        if (error) {
          console.error('Insert player error:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        playerData = data;
        console.log('Player created successfully:', data);
      }

      // Send invitation email if email is provided and user doesn't exist yet
      if (email.trim() && !existingUser?.auth_user_id) {
        try {
          const { error: inviteError } = await supabase.functions.invoke('invite-player', {
            body: {
              email: email.trim(),
              redirectTo: 'https://splitpot.lovable.app/?invite=1'
            }
          });
          
          if (inviteError) {
            console.error('Failed to send invitation:', inviteError);
          }
        } catch (emailError) {
          console.error('Failed to send invitation email:', emailError);
        }
      }

      let successMessage = "Player added successfully";
      if (existingUser?.auth_user_id) {
        successMessage += " and linked to their existing account!";
      } else if (authUserId) {
        successMessage += " and linked to their authenticated account!";
      } else if (email.trim()) {
        successMessage += " and invitation sent!";
      }
      
      toast({
        title: "Success",
        description: successMessage
      });

      onOpenChange(false);
      onPlayerAdded();
    } catch (error) {
      console.error('Error adding player:', error);
      
      // Enhanced error handling for different types of errors
      let errorTitle = "Error";
      let errorMessage = "Failed to add player";
      
      if (error.message?.includes("Authentication required") || 
          error.message?.includes("Not authenticated") ||
          error.message?.includes("Unable to verify authentication")) {
        errorTitle = "Authentication Error";
        errorMessage = "Please log in again to continue.";
        // Redirect to login if authentication failed
        setTimeout(() => {
          const redirectPath = window.location.pathname + window.location.search;
          window.location.href = `/?login=1&redirect=${encodeURIComponent(redirectPath)}`;
        }, 2000);
      } else if (error.message?.includes("permission") || 
                 error.message?.includes("row-level security") ||
                 error.message?.includes("violates row-level security policy")) {
        errorTitle = "Permission Denied";
        errorMessage = "You don't have permission to add players to this group. Only the group creator can add players.";
      } else if (error.message?.includes("already exists")) {
        errorTitle = "Duplicate Player";
        errorMessage = error.message;
      } else {
        errorMessage = `${errorMessage}: ${error.message || 'Unknown error'}`;
      }
      
      console.error('Detailed error info:', {
        originalError: error,
        errorCode: error.code,
        errorMessage: error.message,
        errorDetails: error.details
      });
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Player</DialogTitle>
        </DialogHeader>

        {step === 'email' ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="inline h-4 w-4 mr-2" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter player's email (optional)"
                onKeyDown={(e) => e.key === 'Enter' && handleEmailNext()}
              />
              <p className="text-sm text-muted-foreground">
                If the player has an account, their details will be auto-populated.
              </p>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleEmailNext} 
                disabled={emailChecking}
              >
                {emailChecking ? "Checking..." : "Next"}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {existingUser && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="flex items-center gap-2">
                    <Link className="h-4 w-4" />
                    Existing user found - details populated from their profile.
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>
                <Mail className="inline h-4 w-4 mr-2" />
                Email
              </Label>
              <div className="flex gap-2">
                <Input
                  value={email}
                  disabled
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setStep('email')}
                  size="sm"
                >
                  Change
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">
                <User className="inline h-4 w-4 mr-2" />
                Name *
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter player name"
                required
                disabled={!!existingUser}
              />
              {existingUser && (
                <p className="text-sm text-muted-foreground">
                  Name is locked for existing users. They can update it in their profile.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                <Phone className="inline h-4 w-4 mr-2" />
                Phone
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number (optional)"
                disabled={!!existingUser}
              />
              {existingUser && (
                <p className="text-sm text-muted-foreground">
                  Phone is locked for existing users. They can update it in their profile.
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || !name.trim()}>
                {loading ? "Adding..." : "Add Player"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};